import _ from "lodash";
import * as React from "react";

import IPortletSettings from "./classes/PortletSettings";
import Edit from "./components/Edit/Edit";
import Loader from "./components/Loader";
import Post from "./components/Post";
import Posts from "./components/Posts";
import { IConfig } from "./Config";
import DataService from "./data/DataService";

export enum ViewType {
    Summary,
    Details,
    EditForm,
}

interface IProps extends React.Props<NewsroomView> {
    config: IConfig;
    friendlyUrl?: string;
    newsId?: string;
    isViewingArchivedItems?: boolean;
    initialView: ViewType;
}

interface IState {
    availableCategories: any[];
    availableFeeds: any[];
    currentNewsFriendlyUrl: string;
    currentNewsId: string;
    currentPageNumber: number;
    currentPagePosts: any[];
    currentView: ViewType;
    detailStyleSheet: string;
    detailTemplate: string;
    filterableCategories: any[];
    filterableTags: any[];
    isAdmin: boolean;
    isInitialized: boolean;
    isLoading: boolean;
    isViewingArchivedItems: boolean;
    recentArchivedPosts: any[];
    recentNewsPosts: any[];
    searchText: string;
    selectedCategories: any[];
    selectedTags: any[];
    settings: IPortletSettings;
    summaryStyleSheet: string;
    summaryTemplate: string;
    totalNumberOfPosts: number;
    user: any;
    writableFeedIds: string[];
}

export class NewsroomView extends React.Component<IProps, IState> {
    private readonly dataService = new DataService(this.props.config);

    private readonly defaultNumberOfPageLinks = 5;
    private readonly defaultResultsPerPage = 9;
    private readonly defaultHeadingLevel = "h1";
    private readonly defaultLayoutStyle = "grid";
    private readonly maxSupportedRecentPosts = 20;

    public constructor(props, context) {
        super(props, context);

        this.state = {
            availableCategories: [],
            availableFeeds: [],
            currentNewsFriendlyUrl: this.props.friendlyUrl || "",
            currentNewsId: this.props.newsId || "",
            currentPageNumber: 1,
            currentPagePosts: [],
            currentView: this.props.initialView,
            detailStyleSheet: "",
            detailTemplate: "",
            filterableCategories: [],
            filterableTags: [],
            isAdmin: false,
            isInitialized: false,
            isLoading: true,
            isViewingArchivedItems: this.props.isViewingArchivedItems || false,
            recentArchivedPosts: [],
            recentNewsPosts: [],
            searchText: "",
            selectedCategories: [],
            selectedTags: [],
            settings: null,
            summaryStyleSheet: "",
            summaryTemplate: "",
            totalNumberOfPosts: 0,
            user: null,
            writableFeedIds: [],
        };
    }

    public async componentDidMount() {
        await this.initAndLoadAll();
    }

    public render() {
        return (
            <Loader isLoading={this.state.isLoading || !this.state.settings}>
                <this.renderByCurrentView/>
            </Loader>
        );
    }

    private renderByCurrentView = () => {
        if (this.state.isLoading || !this.state.settings) {
            return null;
        }

        switch (this.state.currentView) {
            case ViewType.Details:
                return <this.renderNewsDetails/>;
            case ViewType.EditForm:
                return <this.renderNewsEditForm/>;
            case ViewType.Summary:
            default:
                return <this.renderNewsSummary/>;
        }
    }

    private renderNewsSummary = () => {
        return (
            <Posts
                config={this.props.config}
                currentPageNumber={this.state.currentPageNumber}
                currentPagePosts={this.state.currentPagePosts}
                dataService={this.dataService}
                filterableCategories={this.state.filterableCategories}
                filterableTags={this.state.filterableTags}
                isAdmin={this.state.isAdmin}
                isLoading={this.state.isLoading}
                isViewingArchivedItems={this.state.isViewingArchivedItems}
                onReinitialize={this.initAndLoadAll.bind(this)}
                onNewsDeleted={() => {
                    this.setState({ currentPageNumber: 1 }, () => {
                        this.loadRecentAndCurrentPageNewsPosts();
                    });
                }}
                onSetIsLoading={(loading: boolean) => this.setState({ isLoading: loading })}
                onSetIsViewingArchivedItems={(archived: boolean) =>
                    this.setState({ currentPageNumber: 1, isViewingArchivedItems: archived }, () => {
                        this.loadCurrentPageNewsPosts();
                    })
                }
                onSetPageNumber={(num: number) =>
                    this.setState({ currentPageNumber: num }, () => {
                        this.loadCurrentPageNewsPosts();
                    })
                }
                onSetSearchText={(text: string) => this.setState({ searchText: text })}
                onSetSelectedCategoriesAndTags={(categories: any[], tags: any[]) =>
                    this.setState({ currentPageNumber: 1, selectedCategories: categories, selectedTags: tags }, () => {
                        this.loadCurrentPageNewsPosts();
                    })
                }
                onSwitchView={this.switchView}
                selectedCategories={this.state.selectedCategories}
                selectedTags={this.state.selectedTags}
                settings={this.state.settings}
                searchText={this.state.searchText}
                stylesheet={this.state.summaryStyleSheet}
                template={this.state.summaryTemplate}
                totalNumberOfPosts={this.state.totalNumberOfPosts}
                writableFeedIds={this.state.writableFeedIds}
            />
        );
    }

    private renderNewsDetails = () => {
        return (
            <Post
                config={this.props.config}
                dataService={this.dataService}
                isAdmin={this.state.isAdmin}
                isViewingArchivedItems={this.state.isViewingArchivedItems}
                newsId={this.state.currentNewsId}
                friendlyUrl={this.state.currentNewsFriendlyUrl}
                onNewsDeleted={() => {
                    this.setState({ currentPageNumber: 1 }, () => {
                        this.loadRecentAndCurrentPageNewsPosts();
                    });
                }}
                onSwitchView={this.switchView}
                recentArchivedPosts={this.state.recentArchivedPosts}
                recentNewsPosts={this.state.recentNewsPosts}
                settings={this.state.settings}
                stylesheet={this.state.detailStyleSheet}
                template={this.state.detailTemplate}
            />
        );
    }

    private renderNewsEditForm = () => {
        return (
            <Edit
                availableCategories={this.state.availableCategories}
                availableFeeds={this.state.availableFeeds}
                config={this.props.config}
                dataService={this.dataService}
                newsId={this.state.currentNewsId}
                onBack={() => this.switchView(ViewType.Summary)}
                onReloadNewsPosts={this.loadRecentAndCurrentPageNewsPosts.bind(this)}
                reloadAvailableCategories={async () => {
                    const availableCategories = await this.loadAvailableCategories();
                    this.setState({ availableCategories });
                }}
                user={this.state.user}
            />
        );
    }

    private async initAndLoadAll() {
        this.setState( { isLoading: true, currentPageNumber: 1 });
        await this.initialize();
        await this.loadTemplateForSummary();
        await this.loadRecentNewsPosts();
        await this.loadCurrentPageNewsPosts();
    }

    private async initialize() {
        const token = await this.dataService.getAccessToken();
        const isAdmin = (this.props.config.role === "superadmin" || this.props.config.role === "author") && !_.isEmpty(token);

        const filterableCategories = (await this.dataService.getAnonymousCategoriesByFeeds(1, 1000))?.data?.items;
        const rawTags = await this.dataService.getTagsByFeeds(1, 1000);
        const filterableTags = _.map(rawTags?.data?.items, (tag) => tag.tag);
        const settings = await this.loadSettings();
        const availableCategories = await this.loadAvailableCategories();
        const availableFeeds = await this.loadAvailableFeeds();
        const user = (this.props.config.role !== "user") ? (await this.dataService.getUser()).data : null;

        // Get all the feeds that the current server can publish to.
        // Items in other feeds should not be shown with the edit and archive/delete buttons.
        const allWritableFeeds = ((await this.dataService.getAnonymousFeeds(true)).data)?.items || [];
        const writableFeedIds = allWritableFeeds.map((f) => f.feedId);

        this.setState({
            availableCategories,
            availableFeeds,
            isAdmin,
            isInitialized: true,
            filterableCategories,
            filterableTags,
            searchText: settings.DefaultSearchTerm,
            settings,
            selectedCategories: settings.SelectedCategories,
            selectedTags: settings.SelectedTags,
            user,
            writableFeedIds,
        });
    }

    private async loadSettings() {
        const settings = (await this.dataService.getSettings()).data || {};

        settings.HeadingLevel = settings.HeadingLevel || this.defaultHeadingLevel;
        settings.LayoutStyle = settings.LayoutStyle || this.defaultLayoutStyle;
        settings.MaxPosts = settings.MaxPosts || this.defaultResultsPerPage;
        settings.DefaultSearchTerm = settings.DefaultSearchTerm || "";
        settings.SelectedCategories = settings.SelectedCategories || [];
        settings.SelectedTags = settings.SelectedTags || [];

        return settings;
    }

    private async loadAvailableCategories() {
        return ((await this.dataService.getAnonymousCategories(1, 100)).data)?.items || [];
    }

    private async loadAvailableFeeds() {
        const allFeeds = ((await this.dataService.getAnonymousFeeds(true)).data)?.items || [];
        let availableFeeds: any[];
        if (this.props.config.isAllFeeds) {
            availableFeeds = allFeeds;
        } else if (this.props.config.feedIds) {
            availableFeeds = _.filter(allFeeds, (f) => this.props.config.feedIds.includes(f.feedId));
        }
        return availableFeeds;
    }

    private async loadRecentAndCurrentPageNewsPosts() {
        this.setState({ isLoading: true });
        await this.loadRecentNewsPosts();
        await this.loadCurrentPageNewsPosts();
    }

    private async loadRecentNewsPosts() {
        // Get one more than the max number supported of recent posts, because
        // the post that is being viewed can be included in the list, and
        // it can get filtered out in the recent posts list.
        const numOfRecentPosts = this.maxSupportedRecentPosts + 1;
        const recentArchivedPosts = (await this.dataService.getAllAnonymousNews(1, numOfRecentPosts, true)).data?.items || [];
        const recentNewsPosts = (await this.dataService.getAllAnonymousNews(1, numOfRecentPosts, false)).data?.items || [];
        this.setState({ recentArchivedPosts, recentNewsPosts });
    }

    private async loadCurrentPageNewsPosts() {
        if (!this.props.config.isAllFeeds && this.props.config.feedIds.length === 0) {
            this.setState({ currentPagePosts: [], isLoading: false });
            return;
        }

        const filterableCategoriesVar = (await this.dataService.getAnonymousCategoriesByFeeds(1, 1000))?.data?.items;

        this.setState({ isLoading: true, filterableCategories: filterableCategoriesVar});

        // const news = (await this.dataService.getAllAnonymousNews(this.state.currentPageNumber, Math.max(this.state.maxPosts || DefaultResultsPerPage, 1), this.state.includeDeleted, this.state.isAdmin, this.state.activeCategories, this.state.activeTags)).data || {};
        // the work around solution to fix 8473 & 8536 is get all the results then filter out on the front end
        const result = (await this.dataService.getAllAnonymousNews(
            this.state.currentPageNumber,
            this.state.settings.MaxPosts,
            this.state.isViewingArchivedItems,
            this.state.isAdmin,
            this.state.selectedCategories,
            this.state.selectedTags,
            this.state.searchText,
            this.state.settings.PostsAfter)
        ).data;

        this.setState({
            currentPagePosts: result?.items || [],
            isLoading: false,
            totalNumberOfPosts: result.total,
        });
    }

    private async loadTemplateForSummary() {
        try {
            const templateId = parseInt(this.state.settings.SummaryTemplateId, 10);
            if (this.state.settings.LayoutStyle !== "template" || isNaN(templateId)) {
                this.setState({ detailStyleSheet: "", detailTemplate: "", summaryStyleSheet: "", summaryTemplate: "" });
                return;
            }
            const summaryTemplate = (await this.dataService.getRenderTemplate("NewsSummary", this.state.settings.SummaryTemplateId)).data;
            const detailTemplate = (await this.dataService.getRenderTemplate("NewsDetail", this.state.settings.DetailTemplateId)).data;
            this.setState({
                detailStyleSheet: detailTemplate?.StyleSheet || "",
                detailTemplate: detailTemplate?.Template || "",
                summaryStyleSheet: summaryTemplate?.StyleSheet || "",
                summaryTemplate: summaryTemplate?.Template || "",
            });
        } catch (ex) {
            console.error(ex);
            this.state.settings.LayoutStyle = this.defaultLayoutStyle;
            this.setState({ summaryStyleSheet: "", summaryTemplate: "" });
        }
    }

    private switchView = (view: ViewType, newsIdorUrl: string = "") => {
        if (view === ViewType.Details) {
            this.setState({ currentNewsFriendlyUrl: newsIdorUrl, currentView: view }, () => {
                this.updatePageUrl();
            });
        } else {
            this.setState({ currentNewsId: newsIdorUrl, currentView: view }, () => {
                    this.updatePageUrl();
                });
        }
    }

    private updatePageUrl = () => {
        let url =  window.location.origin + window.location.pathname + window.location.search;
        if (this.state.currentView === ViewType.Details) {
            const readMode = this.state.isViewingArchivedItems ? "archived" : "news";
            url += `#/${this.props.config.portletInstanceId}/read/${readMode}/f/${this.state.currentNewsFriendlyUrl}`;
        } else if (this.state.currentView === ViewType.EditForm) {
            if (this.state.currentNewsId) {
                url += `#/${this.props.config.portletInstanceId}/edit/${this.state.currentNewsId}`;
            } else {
                url += `#/${this.props.config.portletInstanceId}/create`;
            }
        }
        history.replaceState(this.state, document.title, url);
    }
}
